import { LicenceOrganisationRule } from "./LicenceOrganisationRule";

/**
 * The organization is a subset of LienceOrganisationRule (user title).
 * Since a LicenceOrganisationRule ID determines a specific title, we need to use another identifier to determine the organization.
 * This is only used in the front-end since organization selection is an intermediary step before selecting a title.
 */
class SelectedOrganizationIdGenerator {
    /**
     * When the user has a title not associated with any organization, use this identifier.
     */
    public static readonly noOrganizationCode = "-";

    /**
     * Generates a front-end identifier for the selected organization.
     * @param licenceOrganizationRule The organization to generate an identifier for.
     * @returns The identifier.
     * @example
     * // Returns "CA_QC_CMQ"
     * SelectedOrganizationIdGenerator.generateId({
     *    CountryCode: "CA",
     *    StateCode: "QC",
     *    OrganisationCode: "CMQ"
     * });
     */
    public static generateId(licenceOrganizationRule: LicenceOrganisationRule): string {
        if (!licenceOrganizationRule)
            return "";

        if (!licenceOrganizationRule.OrganisationCode)
            return SelectedOrganizationIdGenerator.noOrganizationCode;

        return `${licenceOrganizationRule.CountryCode}_${licenceOrganizationRule.StateCode}_${licenceOrganizationRule.OrganisationCode}`;
    }

    /**
     * Extracts the country code from the identifier.
     * @param identifier The identifier to extract the country code from.
     * @returns The country code.
     * @example
     * // Returns "CA"
     * SelectedOrganizationIdGenerator.extractCountryCode("CA_QC_CMQ");
     */
    public static extractCountryCode(identifier: string): string {
        return identifier.split("_", 3)[0];
    }

    /**
     * Extracts the state code from the identifier.
     * @param identifier The identifier to extract the state code from.
     * @returns The state code.
     * @example
     * // Returns "QC"
     * SelectedOrganizationIdGenerator.extractStateCode("CA_QC_CMQ");
     */
    public static extractStateCode(identifier: string): string {
        return identifier.split("_", 3)[1];
    }

    /**
     * Extracts the organization code from the identifier.
     * @param identifier The identifier to extract the organization code from.
     * @returns The organization code.
     * @example
     * // Returns "CMQ"
     * SelectedOrganizationIdGenerator.extractOrgCode("CA_QC_CMQ");
     */
    public static extractOrgCode(identifier: string): string {
        return identifier.split("_", 3)[2];
    }
}

export { SelectedOrganizationIdGenerator };
